/**
 * Desktop Notification feature for Fyncall Widget
 * Handles browser notification permissions and sending notifications
 */

import { getConfig } from '../core/config.js';

let notificationPermission = 'default';
let isWidgetFocused = true;

/**
 * Initialize desktop notifications
 * Requests permission if enabled in config
 */
export function initDesktopNotifications() {
    const config = getConfig();

    if (!config.desktopNotifications) {
        console.log('[FyncallNotifications] Desktop notifications disabled');
        return;
    }

    if (!('Notification' in window)) {
        console.log('[FyncallNotifications] Browser does not support notifications');
        return;
    }

    notificationPermission = Notification.permission;

    // Request permission if not already granted
    if (notificationPermission === 'default') {
        requestNotificationPermission();
    }

    // Track widget focus state
    setupFocusTracking();
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
    if (!('Notification' in window)) return 'denied';

    try {
        const permission = await Notification.requestPermission();
        notificationPermission = permission;
        console.log('[FyncallNotifications] Permission:', permission);
        return permission;
    } catch (error) {
        console.error('[FyncallNotifications] Permission request failed:', error);
        return 'denied';
    }
}

/**
 * Setup focus tracking for the widget
 */
function setupFocusTracking() {
    // Track document visibility
    document.addEventListener('visibilitychange', () => {
        isWidgetFocused = !document.hidden;
    });

    // Track window focus
    window.addEventListener('focus', () => {
        isWidgetFocused = true;
    });

    window.addEventListener('blur', () => {
        isWidgetFocused = false;
    });
}

/**
 * Show a desktop notification for a new message
 * @param {Object} options - Notification options
 * @param {string} options.title - Notification title
 * @param {string} options.body - Notification body
 * @param {string} options.icon - Notification icon URL
 * @param {Function} options.onClick - Click handler
 */
export function showDesktopNotification({ title, body, icon, onClick }) {
    const config = getConfig();

    // Don't show if notifications disabled or not permitted
    if (!config.desktopNotifications) return;
    if (notificationPermission !== 'granted') return;

    // Don't show if widget is focused
    if (isWidgetFocused) return;

    try {
        const notification = new Notification(title || config.title || 'New Message', {
            body: body || 'You have a new message',
            icon: icon || config.companyLogo || config.agentAvatarUrl,
            badge: config.companyLogo,
            tag: 'fyncall-message',
            requireInteraction: false,
            silent: config.soundNotifications === false
        });

        notification.onclick = (event) => {
            event.preventDefault();
            window.focus();

            // Open widget if closed
            const chatWindow = document.getElementById('fyncall-chat-window');
            if (chatWindow && !chatWindow.classList.contains('open')) {
                chatWindow.classList.add('open');
            }

            if (onClick) onClick();
            notification.close();
        };

        // Auto-close after 5 seconds
        setTimeout(() => notification.close(), 5000);
    } catch (error) {
        console.error('[FyncallNotifications] Failed to show notification:', error);
    }
}

/**
 * Check if desktop notifications are enabled and permitted
 * @returns {boolean} Whether notifications can be shown
 */
export function canShowNotifications() {
    const config = getConfig();
    return config.desktopNotifications && notificationPermission === 'granted';
}

/**
 * Get current notification permission status
 * @returns {string} Permission status: 'granted', 'denied', or 'default'
 */
export function getNotificationPermission() {
    return notificationPermission;
}

/**
 * Set widget focus state manually
 * @param {boolean} focused - Whether widget is focused
 */
export function setWidgetFocused(focused) {
    isWidgetFocused = focused;
}
